/**
 * @file Scratchpad API (AI-assisted note → structured profile item — ST6 endpoints).
 * @module api/scratchpadApi
 */

import { ENV } from '../../config/runtimeEnv';
import type { UserUuid } from "../../types/identity";
import { http } from "../../lib/http/client";

const HEALTHCARE_BASE_URL = ENV.VITE_HEALTHCARE_SERVER;

export interface ScratchpadDraftResponse {
	section: string | null;
	type: string | null;
	fields: Record<string, any>;
	confidence: number;
	supported: boolean;
}

export interface ScratchpadConfirmResult {
	linked_item_type: string | null;
	linked_item_id: number | null;
	apply_via?: string;
	patch?: Record<string, any>;
}

export interface ScratchpadConfirmResponse {
	note: Record<string, any>;
	result: ScratchpadConfirmResult;
}

export interface ConfirmScratchpadPayload {
	section: string;
	draft: Record<string, any>;
	body?: string;
	actor_id: UserUuid;
	actor_role?: string;
}

/** Generate one structured draft from freeform note text (optionally hinting a section). */
export const generateScratchpadDraft = async (noteText: string, section?: string): Promise<ScratchpadDraftResponse> => {
	const response = await http.post(
		`${HEALTHCARE_BASE_URL}/practitioner/patient-notes/scratchpad/draft`,
		{ note_text: noteText, section },
	);

	return response.data;
};

/** Confirm a reviewed draft for a note: creates the profile item and links the note back. */
export const confirmScratchpad = async (noteId: number | string, payload: ConfirmScratchpadPayload): Promise<ScratchpadConfirmResponse> => {
	const response = await http.post(
		`${HEALTHCARE_BASE_URL}/practitioner/patient-notes/${noteId}/scratchpad/confirm`,
		payload,
	);

	return response.data;
};
